import React, { FC, useContext, useState } from 'react';
import { graphql, createFragmentContainer } from 'react-relay';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { SearchTag } from 'dibs-elements/exports/SearchTag';

import { ServerVarsContext } from '../../global/ServerVarsContext/ServerVarsContext';
import {
    getAppliedFilterValues,
    closeAppliedFilterHandler,
    clearAllAppliedFiltersHandler,
} from '../SbSharedAppliedFilters/sbSharedAppliedFiltersHelpers';
import { getFilterValues } from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers';
import {
    getValidRespAppliedFilters,
    isBatchAppliedFilter,
} from '../SbRespRefineMenu/SbRespRefineMenuFilters/sbRespRefineMenuFiltersMap';
import { SbRespRefineTopAppliedFilters_user } from './__generated__/SbRespRefineTopAppliedFilters_user.graphql';
import { SbRespRefineTopAppliedFilters_itemSearch } from './__generated__/SbRespRefineTopAppliedFilters_itemSearch.graphql';
import { useMeasurementUnit } from '../useMeasurementUnit';
import { SbRespAppliedFiltersItem } from '../SbRespAppliedFilters/SbRespAppliedFiltersItem';

import styles from './SbRespRefineTopAppliedFilters.scss';

type AppliedFilterValuesType = NonNullable<
    NonNullable<SbRespRefineTopAppliedFilters_itemSearch['appliedFilters']>[0]
>['values'];

type Props = {
    itemSearch: SbRespRefineTopAppliedFilters_itemSearch;
    user: SbRespRefineTopAppliedFilters_user;
    currency: string;
};

const DESKTOP_VISIBLE_FILTER_LIMIT = 6;
const TABLET_VISIBLE_FILTER_LIMIT = 4;

const SbRespRefineTopAppliedFiltersComponent: FC<Props> = ({ itemSearch, user, currency }) => {
    // @ts-ignore
    const { isTablet } = useContext(ServerVarsContext);
    const [isViewingMoreFilters, setIsViewingMoreFilters] = useState(false);
    const dispatch = useDispatch();
    // @ts-ignore TODO: ts sbSharedReducers.js -> filterReducer.js
    const generatedUriRef = useSelector(state => state?.filters?.generatedUriRef);
    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });
    const onFilterClose = closeAppliedFilterHandler(dispatch);
    const onClearAllFilters = clearAllAppliedFiltersHandler(dispatch, { itemSearch });

    const { appliedFilters, filters = [] } = itemSearch;
    // @ts-ignore TODO: ts sbRespRefineMenuFiltersMap.js
    const validAppliedFilters = getValidRespAppliedFilters({ appliedFilters });

    const allFilterTags = validAppliedFilters.reduce((acc, appliedFilter) => {
        const isBatchApplied = isBatchAppliedFilter(appliedFilter.name);
        const appliedFilterValues: AppliedFilterValuesType = getAppliedFilterValues({
            appliedFilter,
            isBatchApplied,
        });
        if (appliedFilterValues) {
            appliedFilterValues.forEach((filterValue, index) => {
                acc.push(
                    <SbRespAppliedFiltersItem
                        key={`${appliedFilter.name}-${index}`}
                        currency={currency}
                        filterName={appliedFilter.name}
                        localizedFilterName={appliedFilter.localizedFilterName}
                        filterValue={isBatchApplied ? null : filterValue}
                        // @ts-ignore TODO ts sbSharedRefineMenuHelpers.js
                        availableValues={getFilterValues(filters, appliedFilter.name)}
                        onCloseClick={() =>
                            onFilterClose({
                                filterName: appliedFilter.name,
                                filterValue,
                                generatedUriRef,
                                isBatchApplied,
                            })
                        }
                        measurementUnit={measurementUnit}
                    />
                );
            });
        }
        return acc;
    }, []);
    const filterLimit = isTablet ? TABLET_VISIBLE_FILTER_LIMIT : DESKTOP_VISIBLE_FILTER_LIMIT;
    const displayedTags = allFilterTags.slice(0, filterLimit);
    const extraTags = allFilterTags.slice(filterLimit, allFilterTags.length);
    const extraTagCount = extraTags.length;
    if (allFilterTags.length === 0) {
        return null;
    }

    return (
        <div className={styles.container}>
            {displayedTags}
            {isViewingMoreFilters && extraTags}
            {!!extraTagCount && (
                <div onClick={() => setIsViewingMoreFilters(!isViewingMoreFilters)}>
                    <SearchTag
                        isActive={false}
                        hasDeleteButton={false}
                        dataTn={'more-filters-button'}
                    >
                        <div className={styles.moreFiltersButton}>
                            {isViewingMoreFilters ? (
                                <FormattedMessage
                                    id="SbRespTopAppliedFilters.viewLess"
                                    defaultMessage="View Less"
                                />
                            ) : (
                                <FormattedMessage
                                    id="SbRespTopAppliedFilters.additionalFilters"
                                    defaultMessage="+ {extraTagCount} more"
                                    values={{ extraTagCount }}
                                />
                            )}
                        </div>
                    </SearchTag>
                </div>
            )}
            <div
                className={styles.clearAllButton}
                data-tn="applied-filters-clear-all"
                onClick={onClearAllFilters}
            >
                <FormattedMessage
                    id="SbRespTopAppliedFilters.clearAll"
                    defaultMessage="Clear All"
                />
            </div>
        </div>
    );
};

export const SbRespRefineTopAppliedFilters = createFragmentContainer(
    SbRespRefineTopAppliedFiltersComponent,
    {
        itemSearch: graphql`
            fragment SbRespRefineTopAppliedFilters_itemSearch on ItemSearchQueryConnection {
                ...useMeasurementUnit_itemSearch
                clearAppliedFiltersLinkReference
                filters {
                    name
                    localizedFilterName
                    values {
                        displayName
                        urlLabel
                        linkReference
                    }
                }
                appliedFilters {
                    name
                    localizedFilterName
                    canBeDismissed
                    values {
                        urlLabel
                        linkReference
                        displayName
                        hexCode
                    }
                }
            }
        `,
        user: graphql`
            fragment SbRespRefineTopAppliedFilters_user on User {
                ...useMeasurementUnit_user
            }
        `,
    }
);
